import React, { ComponentType } from 'react';
import { styled, Theme } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from './CircularProgress';

const getVariantStyles = (
  { variant, color }: CustomButtonProps,
  { palette }: Theme
) => {
  if (!color || color == 'inherit') {
    return {
      // color: 'inherit'
    };
  }

  const baseColor = palette[color];
  switch (variant) {
    case 'contained':
      return {
        color: baseColor.contrastText,
        backgroundColor: baseColor.main,
        '&:hover': {
          backgroundColor: baseColor.dark,
        },
      };
    case 'text':
      return {
        color: baseColor.main,
        '&:hover': {
          backgroundColor: baseColor.light,
        },
      };
    case 'contrast-text':
      return {
        color: baseColor.main,
        '&:hover': {
          backgroundColor: baseColor.main,
          color: baseColor.contrastText,
        },
      };
    case 'outlined':
      return {
        color: baseColor.main,
        border: `1px solid ${baseColor.main}`,
        '&:hover': {
          backgroundColor: baseColor.light,
          border: `1px solid ${baseColor.main}`,
        },
      };
    default:
      return {
        color,
      };
  }
};

const getSizeStyles = (
  { size }: CustomButtonProps,
  { spacing, typography }: Theme,
) => {
  switch (size) {
    case 'small':
      return {
        fontSize: typography.button.fontSize,
      };
    case 'large':
      return {
        fontSize: typography.body1.fontSize,
      };
    default:
      return {};
  }
};

const getBackgroundColor = (
  { variant, color }: CustomButtonProps,
  { palette }: Theme
) => {
  if (variant === 'text' || variant === 'outlined') {
    return 'transparent';
  }

  switch (color) {
    case 'success':
    case 'warning':
    case 'error':
    case 'secondary':
    case 'primary':
      return palette[color].main;
    default:
      return palette.grey[300];
  }
};

const LoadingContainer = styled('div')({
  color: 'transparent'
});

const Adornment = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(2.5),
  flexShrink: '0',
  display: 'flex',
  alignItems: 'center',
}));

export type CustomButtonProps = Omit<ButtonProps, 'variant'> & {
  fitWidth?: boolean,
  loading?: boolean,
  endAdornment?: React.ReactNode,
  noClickable?: boolean,
  variant?: ButtonProps['variant'] | 'contrast-text',
};

const Loader = styled(CircularProgress)(({ theme }) => ({
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  // @ts-ignore
  color: theme.palette.colors.N010,
  strokeWidth: theme.spacing(1),
}));

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      children,
      loading,
      endAdornment,
      variant,
      ...props
    }: CustomButtonProps,
    forwardedRef: any
  ) => (
    <Button
      ref={forwardedRef}
      variant={variant && (variant === 'contrast-text' ? undefined : variant)}
      {...props}
    >
      {loading
        ? (
          <LoadingContainer>
            <Loader disableBackground size='1em'/>
          </LoadingContainer>
        )
        : children
      }
      {endAdornment && <Adornment>{endAdornment}</Adornment>}
    </Button>
  )
);

const StyledCustomButton: ComponentType<CustomButtonProps> = styled(
  CustomButton
)<CustomButtonProps>(({ theme, ...props }) => ({
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: 'none',
  },
  ...getVariantStyles(props, theme),
  ...getSizeStyles(props, theme),
  ...(props.fitWidth && {
    width: 'fit-content'
  }),
  ...(props.noClickable && {
    cursor: 'default',
    backgroundColor: getBackgroundColor(props, theme),
  }),
  ...(props.endAdornment && {
    paddingRight: theme.spacing(2.5),

    '& > div': {
      marginLeft: theme.spacing(2.5),
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
    }
  })
}));

export default StyledCustomButton;